import { isCommentable, isInlineEditable, type ContentItem } from "./layout/documentLayout";

// What the reviewer may do on the review screen, from the spec's "Document states" table. Pure so
// it is unit-tested in node (ci/document-review-session.test.cjs).

export type ReviewLayoutType = "scanned" | "preview" | "list";

export interface ReviewDocumentState {
  /** Answers are saved as the task's caregiver: without one there is nowhere to save them. */
  hasCaregiver: boolean;
  hasScheduledDoc: boolean;
  isSubmitted: boolean;
  /** "preview" = GENERAL non-HTML doc, "list" = HTML-template doc, "scanned" = uploaded PDF */
  layoutType: ReviewLayoutType;
}

export interface ReviewCapabilities {
  /** Comment on single questions (never on a scanned PDF: there are no questions to point at). */
  canCommentOnQuestions: boolean;
  /**
   * Inline edit, on GENERAL previews and HTML-template lists alike (same editors, same `/answer`
   * writes as the classic form), in any status: the agency may fill in a document that was not
   * started or submitted yet. A scanned PDF has no answers to edit.
   */
  canInlineEdit: boolean;
  /** General comment + Reopen for RN. */
  canReopen: boolean;
}

/**
 * Comment and reopen need a submitted document (approved docs are still submitted); editing only
 * needs the task's caregiver, whose document the answers are saved to (the first save creates it).
 */
export function reviewCapabilities(state: ReviewDocumentState): ReviewCapabilities {
  const isReviewable = state.isSubmitted && state.hasScheduledDoc;
  return {
    canCommentOnQuestions: isReviewable && state.layoutType !== "scanned",
    canInlineEdit: state.hasCaregiver && state.layoutType !== "scanned",
    canReopen: isReviewable,
  };
}

/** Why the answers cannot be edited, when that is not obvious (a scanned PDF is). */
export function inlineEditUnavailableReason(state: ReviewDocumentState): string | null {
  if (state.layoutType === "scanned" || state.hasCaregiver) {
    return null;
  }
  return "No caregiver is assigned to this task, so its documents can't be filled in yet.";
}

/** "Regenerate PDF" re-renders the generated PDF; a scanned doc's file is the scan itself. */
export function canRegeneratePdf(review: {
  isScanned: boolean;
  isSubmitted: boolean;
  scheduledDocId: number | null;
}): boolean {
  return review.isSubmitted && !review.isScanned && review.scheduledDocId !== null;
}

export interface QuestionActions {
  comment: boolean;
  edit: boolean;
}

/**
 * `mobileHiddenIds`: see `mobileHiddenQuestionIds` (the RN cannot see comments on those). Types
 * with no editor (headers, image, editor html, icd, icdChart, vbpItem, medicationProfileItem) hold
 * nothing to fix: they get no edit action (see `isInlineEditable`).
 */
export function questionActions(
  capabilities: ReviewCapabilities,
  question: ContentItem,
  mobileHiddenIds: ReadonlySet<number>
): QuestionActions {
  return {
    comment: capabilities.canCommentOnQuestions && isCommentable(question, mobileHiddenIds),
    edit: capabilities.canInlineEdit && isInlineEditable(question),
  };
}

// ---- Document status and the Approve / Mark submitted action ----

export type ReviewStatus = "notSubmitted" | "reopened" | "submitted" | "approved";

/** From the scheduled document's own flags; comments (agency or AI) do not affect it. */
export function deriveReviewStatus(params: {
  approvedAt: unknown;
  isOpenForResubmission: boolean;
  isSubmitted: boolean;
}): ReviewStatus {
  if (params.approvedAt !== null) {
    return "approved";
  }
  if (params.isSubmitted) {
    return "submitted";
  }
  return params.isOpenForResubmission ? "reopened" : "notSubmitted";
}

export type ApprovalAction =
  | { type: "approve"; isDisabled: boolean }
  | { type: "approved" }
  | { type: "markSubmitted" }
  | { type: "submit" };

export interface ReviewFlags {
  approvedAt: unknown;
  isOpenForResubmission: boolean;
  isScanned: boolean;
  isSubmitted: boolean;
  scheduledDocId: number | null;
}

/**
 * Same rules as the drawer card: approving needs a submission; approved docs show "Approved". An
 * unsubmitted document that holds answers (filled in by the RN or the agency) is submitted with
 * them, which generates its PDF (the classic form's Submit); one without any is only marked
 * submitted.
 */
export function approvalAction(review: ReviewFlags): ApprovalAction {
  if (!review.isSubmitted) {
    return review.scheduledDocId !== null && !review.isScanned
      ? { type: "submit" }
      : { type: "markSubmitted" };
  }
  if (review.approvedAt !== null) {
    return { type: "approved" };
  }
  return { type: "approve", isDisabled: review.scheduledDocId === null };
}

/**
 * A submitted document the agency edited is submitted again to apply the edits (submitted date,
 * PDF). A scanned document has no answers to submit.
 */
export function canResubmit(review: ReviewFlags): boolean {
  return review.isSubmitted && !review.isScanned && review.scheduledDocId !== null;
}

/**
 * Submit, with the warning that changes are not applied, shows on a submitted document whose
 * answers were saved after its PDF was generated (`hasAnswersNewerThanPdf`, from the server, so it
 * still shows when the review is opened again), or that was edited in this session (a patient
 * record change is no answer the server can compare).
 */
export function showsResubmit(
  review: ReviewFlags & { hasAnswersNewerThanPdf: boolean },
  unsubmittedEdits: number
): boolean {
  return canResubmit(review) && (review.hasAnswersNewerThanPdf || unsubmittedEdits > 0);
}

/**
 * Whether Approve / Mark submitted / Submit, clicked on the review as shown, may still run against
 * the freshly loaded review. Any change of status (e.g. the RN resubmitted, another reviewer
 * reopened or approved it) aborts, so the reviewer never acts on a state they have not seen.
 */
export function isApprovalStillAllowed(
  clicked: "approve" | "markSubmitted" | "submit" | "resubmit",
  shown: ReviewFlags,
  fresh: ReviewFlags
): boolean {
  if (deriveReviewStatus(shown) !== deriveReviewStatus(fresh)) {
    return false;
  }
  if (shown.scheduledDocId !== fresh.scheduledDocId) {
    return false;
  }
  const action = approvalAction(fresh);
  switch (clicked) {
    case "approve":
      return action.type === "approve" && !action.isDisabled;
    case "markSubmitted":
      return action.type === "markSubmitted";
    case "submit":
      return action.type === "submit";
    case "resubmit":
      return canResubmit(fresh);
  }
}

// ---- Closing the review ----

/** A transition leaves the review when it changes state or the review's document param. */
export function isLeavingReview(params: {
  fromReviewParam: unknown;
  fromState: string;
  toReviewParam: unknown;
  toState: string;
}): boolean {
  const asText = (value: unknown) => (value === null || value === undefined ? "" : String(value));
  return (
    params.fromState !== params.toState ||
    asText(params.fromReviewParam) !== asText(params.toReviewParam)
  );
}

export type CloseBlock = "saving" | "submitting" | "unsentWork";

/**
 * Why closing must wait (a save or a submit in flight) or ask first (work that closing would lose).
 * `hasUnsavedCommentText`: an open comment popover holds text not yet kept as its draft.
 * `isEditing`: an answer editor is open (its input is not saved yet). An open patient-record
 * editor only counts while one of its forms / dialogs is open (`hasOpenRecordForm`) or a record
 * change is in flight (`isSavingRecord`): its other changes are saved as they are made.
 * `isSubmitting`: the document is being submitted (e.g. from the dialog asking before closing,
 * which closes the review itself once it is done).
 */
export function closeBlock(params: {
  hasOpenRecordForm: boolean;
  hasUnsavedCommentText: boolean;
  hasUnsentWork: boolean;
  isEditing: boolean;
  isSaving: boolean;
  isSavingRecord: boolean;
  isSubmitting: boolean;
}): CloseBlock | null {
  if (params.isSaving || params.isSavingRecord) {
    return "saving";
  }
  if (params.isSubmitting) {
    return "submitting";
  }
  return params.hasUnsentWork ||
    params.hasUnsavedCommentText ||
    params.isEditing ||
    params.hasOpenRecordForm
    ? "unsentWork"
    : null;
}

/** Body of the "discard and close" dialog, naming exactly what would be lost. */
export function discardSummary(params: {
  draftCount: number;
  hasGeneralComment: boolean;
  hasOpenRecordForm: boolean;
  isEditing: boolean;
}): string {
  const parts: string[] = [];
  if (params.draftCount > 0) {
    parts.push(`${params.draftCount} unsent ${params.draftCount === 1 ? "comment" : "comments"}`);
  }
  if (params.hasGeneralComment) {
    parts.push("an unsent general comment");
  }
  if (params.isEditing) {
    parts.push("an answer you are editing that is not saved");
  }
  if (params.hasOpenRecordForm) {
    parts.push("a change to the patient's record that is not saved");
  }
  if (parts.length === 0) {
    return "Close the review?";
  }
  const list =
    parts.length === 1
      ? parts[0]
      : `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  const isSingle = parts.length === 1 && params.draftCount <= 1;
  return `You have ${list}. Close and discard ${isSingle ? "it" : "them"}?`;
}

/**
 * What a failed PDF regeneration tells the reviewer. Server errors (5xx) and network failures get
 * a friendly sentence; the raw server message, when there is one, goes behind "Details". A 4xx
 * message is the server explaining what is wrong, so it is the text itself.
 */
export function regenerateFailureText(params: {
  isNetworkError: boolean;
  serverMessage: string;
  status: number | null;
}): { details: string | null; summary: string } {
  const message = params.serverMessage.trim();
  // A failure without a server body formats as the AxiosError itself
  const details = message === "" || message.startsWith("AxiosError") ? null : message;
  if (params.isNetworkError) {
    return {
      summary: "The server could not be reached. Check your connection and try again.",
      details,
    };
  }
  if (params.status !== null && params.status >= 400 && params.status < 500 && details !== null) {
    return { summary: details, details: null };
  }
  return {
    summary: "The PDF service had a problem generating the file. Try again in a moment.",
    details,
  };
}

/**
 * Reopening sends the session's comments; an open or saving inline editor would be dropped (an
 * open patient-record editor only while one of its forms is open or a change is saving).
 */
export function reopenBlockedReason(params: {
  hasOpenRecordForm: boolean;
  isEditing: boolean;
  isSaving: boolean;
  isSavingRecord: boolean;
}): string | null {
  if (params.isSaving) {
    return "Wait until the answer is saved";
  }
  if (params.isSavingRecord) {
    return "Wait until the patient's record is saved";
  }
  if (params.isEditing) {
    return "Save or cancel the answer you are editing first";
  }
  if (params.hasOpenRecordForm) {
    return "Save or cancel the change to the patient's record first";
  }
  return null;
}
